import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { getCategoryLabel } from '../lib/catalogCategories';

import FramedImage from './FramedImage';
import WishlistIcon from './WishlistIcon';

const fallbackProductImage = 'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=800&q=80';

export default function ProductCard({ product }) {
  const { addToCart, cartLoading } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { isWishlisted, toggleWishlist } = useWishlist();
  const navigate = useNavigate();
  const [addState, setAddState] = useState('idle');
  const [wishlistState, setWishlistState] = useState('idle');
  const resetTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  if (!product) return null;

  const {
    title,
    slug,
    subtitle,
    price_mrp,
    final_price,
    savings,
    category_name,
    category_slug,
    origin_label,
    origin_city,
    stock,
    badges = '',
    images = [],
    variants = [],
  } = product;

  const mainImage =
    Array.isArray(images) && images.length > 0
      ? images[0]
      : fallbackProductImage;

  const badgeList = typeof badges === 'string' ? badges.split(',').filter(Boolean) : [];
  const availableStock = Number.isFinite(Number(stock)) ? Number(stock) : 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (addState === 'adding') return;

    if (Array.isArray(variants) && variants.length > 0) {
      navigate(`/product/${slug}`);
      return;
    }

    setAddState('adding');
    try {
      await addToCart(product, 1);
      setAddState('added');
      resetTimer.current = setTimeout(() => setAddState('idle'), 1800);
    } catch {
      setAddState('idle');
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (wishlistState === 'saving') return;
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/product/${slug}`)}`);
      return;
    }
    setWishlistState('saving');
    try {
      await toggleWishlist(product);
    } catch (_error) {
      // The shared wishlist context restores the previous state and exposes the error.
    } finally {
      setWishlistState('idle');
    }
  };

  const saved = isWishlisted(product.id);

  return (
    <div className="product-card h-100 position-relative">
      <button
        type="button"
        className={`product-card-wishlist${saved ? ' is-saved' : ''}`}
        onClick={handleWishlist}
        disabled={wishlistState === 'saving'}
        aria-label={user
          ? `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} your wishlist`
          : `Sign in to save ${title} to your wishlist`}
        title={user ? (saved ? 'Remove from wishlist' : 'Save to wishlist') : 'Sign in to save'}
      >
        <WishlistIcon filled={saved} />
      </button>
      <Link to={`/product/${slug}`} className="product-card-link text-decoration-none text-reset">
        <div className="product-card-img-wrapper">
          <FramedImage
            src={mainImage}
            alt={title || 'Handmade product'}
            className="product-card-img"
            fallbackSrc={fallbackProductImage}
          />

          <div className="product-card-badges">
            {badgeList.map((badge, idx) => {
              const clean = badge.trim().toLowerCase();
              const badgeClass =
                clean === 'bestseller'
                  ? 'badge-bestseller'
                  : clean === 'new'
                  ? 'badge-new'
                  : clean === 'featured'
                  ? 'badge-featured'
                  : clean === 'limited_edition' || clean === 'limited edition'
                  ? 'badge-limited'
                  : 'badge-discount';
              const badgeLabel = clean === 'limited_edition' || clean === 'limited edition'
                ? 'LIMITED EDITION'
                : badge.trim();
              return (
                <span
                  key={idx}
                  className={`badge badge-custom ${badgeClass}`}
                >
                  {badgeLabel}
                </span>
              );
            })}
          </div>

          {availableStock <= 0 && (
            <div className="product-card-sold-out">
              OUT OF STOCK
            </div>
          )}
        </div>

        <div className="product-card-body">
          {category_name && (
            <span className="text-muted text-uppercase small font-weight-bold mb-1" style={{ fontSize: '0.7rem' }}>
              {getCategoryLabel({ name: category_name, slug: category_slug })}
            </span>
          )}

          <h2 className="product-card-title" title={title}>
            {title}
          </h2>

          {subtitle && (
            <p className="text-muted small mb-2 text-truncate" style={{ fontSize: '0.8rem' }}>
              {subtitle}
            </p>
          )}

          {origin_label && (
            <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>
              Made at {origin_label}{origin_city ? ` · ${origin_city}` : ''}
            </p>
          )}

          <div className="product-card-price">
            <div>
              {price_mrp > final_price && <span className="price-mrp">₹{price_mrp}</span>}
              <span className="price-final">₹{final_price}</span>
            </div>

            {savings > 0 && <span className="price-savings">Save ₹{savings}</span>}
          </div>

        </div>
      </Link>
      <div className="product-card-actions">
        <span className={availableStock > 0 ? 'in-stock' : 'out-of-stock'}>
          {availableStock > 5 ? 'In stock' : availableStock > 0 ? `Only ${availableStock} left` : 'Unavailable'}
        </span>
        <button
          type="button"
          onClick={handleAddToCart}
          disabled={availableStock <= 0 || cartLoading || addState === 'adding'}
          className={`btn btn-primary-custom product-card-add${addState === 'added' ? ' is-added' : ''}`}
        >
          {Array.isArray(variants) && variants.length > 0
            ? 'Choose size'
            : cartLoading ? 'Preparing cart…' : addState === 'adding' ? 'Adding…' : addState === 'added' ? 'Added ✓' : 'Add to Cart'}
        </button>
        <span className="visually-hidden" role="status" aria-live="polite">
          {addState === 'added' ? `${title || 'Product'} added to your cart.` : ''}
        </span>
      </div>
    </div>
  );
}
